import axios from 'axios'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ChartColumnIncreasing, MoveRight, TrendingDown, TrendingUp } from 'lucide-react'
import SearchableDropdown from '../../components/SearchableDropdown'
import { getTrendPrices } from '../../lib/apis'
import PriceMaketProduct from '../../components/PriceMaketProduct'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import Breadcrumb from '../../components/Breadcrumb'
import SparkleImage from '../../components/SparkleImage'
import { scrollToTop } from '../../lib/utils'

// Types correspondants
interface Region {
  value: number;
  label: string;
  label_en: string;
}

interface Departement {
  value: number;
  label: string;
  label_en: string;
}

interface Arrondissement {
  value: number;
  label: string;
  label_en: string;
}

interface Marche {
  value: number;
  label: string;
  label_en: string;
}

interface InputRegion {
  region_id: number;
  region_name: string;
  region_name_en: string;
}
interface InputDepartement {
  departement_id: number;
  departement_name: string;
  departement_name_en: string;
}
interface InputArrondissement {
  arrondissement_id: number;
  arrondissement_name: string;
  arrondissement_name_en: string;
}
interface InputMarche {
  market_id: number;
  market_name: string;
  market_name_en: string;
}


function ConsulterPrix() {
  const { t, i18n } = useTranslation()
  const token = localStorage.getItem("token")
  const [PriceOfProduct, setPriceOfProduct] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [arrondissements, setArrondissements] = useState<Arrondissement[]>([]);
  const [Markets, setMarkets] = useState<Marche[]>([]);
  const [Regions, setRegions] = useState<Region[]>([]);
  const [Departments, setDepartments] = useState<Departement[]>([]);

  // selected values for dropdowns
  const [selectedLocality, setSelectedLocality] = useState<Arrondissement | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<Marche | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Departement | null>(null);

  const [marketSearch, setMarketSearch] = useState('');
  const [regionSearch, setRegionSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('')
  const [arrondissementSearch, setArrondissementSearch] = useState('')

  const navigate = useNavigate()
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Refs pour éviter les problèmes de closure et de multiples appels
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    scrollToTop()
  }, [])

  // Nettoyage au démontage du composant
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Nettoyer l'observer au démontage
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  function convertRegions(input: Record<number, InputRegion>): Region[] {
    return Object.values(input).map(region => ({
      value: region.region_id,
      label: region.region_name,
      label_en: region.region_name_en
    }));
  }

  function convertDepartements(input: Record<number, InputDepartement>): Departement[] {
    return Object.values(input).map(item => ({
      value: item.departement_id,
      label: item.departement_name,
      label_en: item.departement_name_en
    }));
  }

  function convertArrondissements(input: Record<number, InputArrondissement>): Arrondissement[] {
    return Object.values(input).map(item => ({
      value: item.arrondissement_id,
      label: item.arrondissement_name,
      label_en: item.arrondissement_name_en
    }));
  }

  function convertMarche(input: Record<number, InputMarche>): Marche[] {
    return Object.values(input).map(item => ({
      value: item.market_id,
      label: item.market_name,
      label_en: item.market_name_en
    }));
  }

  const getTrendspricesProducts = useCallback(async (
    regionId?: number,
    departmentId?: number,
    arrondissementId?: number,
    marketId?: number,
    pageNum: number = 1,
    append: boolean = false
  ) => {
    // Éviter les appels multiples simultanés
    if (isFetchingRef.current) {
      console.log('Already fetching, skipping...');
      return;
    }

    isFetchingRef.current = true;

    if (!append) {
      setInitialLoading(true);
    }
    setLoading(true);

    try {
      let url = `${getTrendPrices}?limit=10&page=${pageNum}`;
      const params = [];
      if (regionId) params.push(`region_id=${regionId}`);
      if (departmentId) params.push(`departement_id=${departmentId}`);
      if (arrondissementId) params.push(`arrondissement_id=${arrondissementId}`);
      if (marketId) params.push(`market_id=${marketId}`);

      if (params.length > 0) url += `&${params.join('&')}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Vérifier si le composant est toujours monté
      if (!mountedRef.current) return;

      const regionsArray: Region[] = convertRegions(response.data.regions);
      const departementsArray: Departement[] = convertDepartements(response.data.departements);
      const arrondissemtsArray: Arrondissement[] = convertArrondissements(response.data.arrondissements);
      const marchesArray: Marche[] = convertMarche(response?.data?.markets);

      setRegions(regionsArray);
      setDepartments(departementsArray);
      setArrondissements(arrondissemtsArray);
      setMarkets(marchesArray);

      const newData = response.data.result || [];

      if (append) {
        setPriceOfProduct(prev => [...prev, ...newData]);
      } else {
        setPriceOfProduct(newData);
        setInitialLoading(false);
      }

      setHasMore(newData.length === 10);
    } catch (error) {
      console.error("Error fetching products:", error);
      if (mountedRef.current) {
        setInitialLoading(false);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        // Ajouter un petit délai avant de permettre un nouveau fetch
        setTimeout(() => {
          isFetchingRef.current = false;
        }, 500);
      }
    }
  }, [token]);

  // Chargement initial
  useEffect(() => {
    getTrendspricesProducts();
  }, []); // Ne pas mettre getTrendspricesProducts dans les dépendances pour éviter les boucles

  // Gestion de l'infinite scroll améliorée
  useEffect(() => {
    // Nettoyer l'ancien observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting && !loading && hasMore && !isFetchingRef.current) {
        console.log('Loading more products...');
        const nextPage = page + 1;
        setPage(nextPage);
        getTrendspricesProducts(
          selectedRegion?.value,
          selectedDepartment?.value,
          selectedLocality?.value,
          selectedMarket?.value,
          nextPage,
          true
        );
      }
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: '100px' // Déclencher un peu avant d'atteindre le sentinel
    });

    // Petit délai pour s'assurer que le DOM est prêt
    const timer = setTimeout(() => {
      const sentinel = document.querySelector('#scroll-sentinel');
      if (sentinel && observerRef.current) {
        observerRef.current.observe(sentinel);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, page, selectedRegion, selectedDepartment, selectedLocality, selectedMarket, getTrendspricesProducts]);

  // Fonction helper pour reset et scroll
  const resetAndFetch = useCallback(async (params: {
    regionId?: number;
    departmentId?: number;
    arrondissementId?: number;
    marketId?: number;
  }) => {
    setPage(1);
    setHasMore(true);
    await getTrendspricesProducts(
      params.regionId,
      params.departmentId,
      params.arrondissementId,
      params.marketId,
      1,
      false
    );
    // Utiliser requestAnimationFrame pour un scroll plus fluide
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [getTrendspricesProducts]);

  console.log('PriceOfProduct', PriceOfProduct);

  return (
    <div className='w-full bg-[#fff] md:px-8 px-1' style={{ overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
      <div className="flex flex-row py-7 items-center justify-start w-full ml-5">
        <Breadcrumb />
      </div>
      <div className='mb-10 items-center'>
        <p className='text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold' style={{ whiteSpace: 'nowrap', fontWeight: 'bold', textAlign: 'center', fontFamily: 'var(--font-body)' }}>
          <Trans
            i18nKey="market_title"
            components={{ strong: <span style={{ color: 'gold' }} /> }}
          />
        </p>
        <p className='text-center font-dm px-10 text-[#6B7280]'>{t('last_price_description')}</p>
      </div>

      <div className='my-5 flex flex-row items-center mx-10 gap-3'>
        <ChartColumnIncreasing color='#15803D' />
        <p className='text-[20px]'>{t('tendances_prix')}</p>
      </div>
      {/* <div className='flex flex-row justify-around mt-5'>
        <div className='flex flex-row gap-2 items-center'>
          <TrendingDown color='#15803D' />
          <p style={{ color: '#15803D', fontSize: 12 }}>{t('diminution_de_prix')}</p>
        </div>
        <div className='flex flex-row gap-2 items-center'>
          <TrendingUp color='#CE181E' />
          <p style={{ color: '#CE181E', fontSize: 12 }}>{t('augmentation_de_prix')}</p>
        </div>
        <div className='flex flex-row gap-2 items-center'>
          <MoveRight color='#6B7280' />
          <p style={{ fontSize: 12, color: '#6B7280' }}>{t('prix_constant')}</p>
        </div>
      </div> */}
      <div className='flex bg-[#ffffff] mx-10 rounded-lg flex-col'>
        <div style={{ boxShadow: "0 10px 30px -6px rgba(21, 128, 61, 0.15), 0 4px 12px rgba(26,28,26,0.08)" }} className='relative bg-[#fff] font-dm border border-[#dfe1df] rounded-[12px] flex flex-row gap-3 px-4 py-5 w-full'>
          <div className="bg-gradient-to-b from-primary via-third to-secondary h-[60px] w-1 self-center rounded-lg absolute left-0 top-1/2 -translate-y-1/2" />
          {/* <div className='bg-gradient-to-b from-primary via-secondary to-[#6B7280] h-14 w-1.5 self-center rounded-lg absolute right-0' /> */}
          <SearchableDropdown
            data={Regions}
            onSelect={async (m: any) => {
              setSelectedMarket(null);
              setMarketSearch('');
              setArrondissementSearch('');
              setSelectedLocality(null);
              setSelectedDepartment(null);
              setDepartmentSearch('');
              setSelectedRegion(m);
              setRegionSearch(m.label);
              await resetAndFetch({ regionId: m.value });
            }}
            placeholder={t("searchregion")}
            value={regionSearch}
          />
          <SearchableDropdown
            data={Departments}
            onSelect={async (m: any) => {
              setSelectedMarket(null);
              setMarketSearch('');
              setArrondissementSearch('');
              setSelectedLocality(null);
              setSelectedDepartment(m);
              setDepartmentSearch(m.label);
              await resetAndFetch({
                regionId: selectedRegion?.value,
                departmentId: m.value
              });
            }}
            placeholder={t("searchdepartement")}
            value={departmentSearch}
          />
          <SearchableDropdown
            data={arrondissements}
            onSelect={async (loc: any) => {
              setSelectedLocality(loc);
              setSelectedMarket(null);
              setMarketSearch('');
              setArrondissementSearch(loc.label);
              await resetAndFetch({
                regionId: selectedRegion?.value,
                departmentId: selectedDepartment?.value,
                arrondissementId: loc.value
              });
            }}
            placeholder={t("searchlocality")}
            value={arrondissementSearch}
          />

          <SearchableDropdown
            data={Markets}
            onSelect={async (m) => {
              setSelectedMarket(m);
              setMarketSearch(m.label);
              await resetAndFetch({
                regionId: selectedRegion?.value,
                departmentId: selectedDepartment?.value,
                arrondissementId: selectedLocality?.value,
                marketId: m.value
              });
            }}
            placeholder={t("searchmarket")}
            value={marketSearch}
          />
        </div>
        <div style={{ WebkitOverflowScrolling: 'touch', fontFamily: `var(--font-dm)` }}>
          {initialLoading ? (
            <div>
              <SparkleImage src='/assets/logo_black.png' alt='logo' interval={500} opacityRange={[0.2, 1]} />
            </div>
          ) : PriceOfProduct && PriceOfProduct.length > 0 ? (
            <div className='mt-5 p-1 sm:p-2 lg:p-3 xl:p-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
              {PriceOfProduct.map((product: any, index) => (
                <PriceMaketProduct
                  key={`${product.product_id}-${index}`}
                  product_name={i18n.language === 'fr' ? product?.product_name : product?.product_name_en}
                  average_price={product.average_price}
                  market_name={i18n.language === 'fr' ? product?.market_name : product?.market_name_en}
                  arrondissement={i18n.language === 'fr' ? product?.arrondissement_name : product.arrondissement_name_en}
                  collect_type={i18n.language === 'fr' ? product?.collect_type : product?.collect_type_en}
                  latest_price={product?.latest_price}
                  previous_price={product?.previous_price}
                  image_url={product?.image_url}
                  showMarketName={!!selectedMarket}
                  showRegion={!!selectedRegion}
                  showDepartement={!!selectedDepartment}
                  region={i18n.language === 'fr' ? product?.region_name : product?.region_name_en}
                  department={i18n.language === 'fr' ? product?.departement_name : product?.departement_name_en}
                  onclick={!!selectedMarket ? () => navigate('/analyse', { state: { product } }) : () => navigate('/market', { state: { product } })}
                  product={product}
                />
              ))}
              <div id="scroll-sentinel" style={{ height: '1px', width: '100%' }} />
            </div>
          ) : (
            <div className='items-center p-4'>
              <p>{t('noVariation')}</p>
            </div>
          )}

          {loading && !initialLoading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#15803D]"></div>
            </div>
          )}

          {!loading && !hasMore && PriceOfProduct.length > 0 && (
            <div className="text-center py-4 text-gray-500">
              {t('endOfResults')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConsulterPrix